import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { find, findIndex, get } from 'lodash';
import { Accordion, AccordionGroup } from '@lendi-ui/accordion';
import Grid from '@lendi-ui/grid';
import CheckCircle from '@lendi-ui/icon/CheckCircle';
import Error from '@lendi-ui/icon/Error';
import { Heading } from '@lendi-ui/typography';

import {
  CREDIT_REPORT_RESPONDS_CODE,
  FIELD_PATHS,
  FIELD_VALUES,
  FREQUENCY,
  INCOME_TYPE,
  INVITATION_STATUS,
  RELATIONSHIP_STATUS_OPTIONS,
  URL_PARAM_APPLICATION_ID,
} from '@funnels/constants';
import { useCustomers } from '@funnels/hooks';
import { useAnalytics } from '@funnels/providers';
import { cancelInvitation, createInvitation } from '@funnels/redux-store';
import {
  ApplicantEntityIndex,
  Company,
  FormValuesApplicants,
  Income,
  ReduxStoreData,
  StoreApplicant,
  StoreCreditReport,
  StoreEmployment,
  StoreInvitation,
  UseParams,
} from '@funnels/types';
import { StringDisplay, StringsDisplay } from '@funnels/ui-components/Fields';
import {
  convert,
  getAgeFromDOB,
  getDisplayDateString,
  getEmploymentCompletedStatus,
  getLatestInvitation,
  getRelatedNonPersonApplicants,
  getSelfEmployedYearlyIncomeAmount,
  initialFormValues,
} from '@funnels/utils';

import { GreyedPanelText } from '../../../Fields/PlainDisplay/style';
import {
  AddressDatesReadOnlyView,
  AgeReadOnlyView,
  AnnualIncomeReadOnlyView,
  ApplicantAddressReadOnlyView,
  ApplicantEmailReadOnlyView,
  ApplicantMobileReadOnlyView,
  ApplicantsFirstNameReadOnlyView,
  ApplicantsSurNameReadOnlyView,
  BusinessNameReadOnlyView,
  BusinessStructureReadOnlyView,
  CreditReportReadOnlyTeamView,
  DateOfBirthReadOnlyView,
  DependantsDetailsReadOnlyView,
  EmploymentBasisReadOnlyView,
  EmploymentDatesReadOnlyView,
  EmploymentTypeReadOnlyView,
  HousingStatusReadOnlyView,
  InviteStatusReadOnlyView,
  InviteStatusReadOnlyViewAccompaniment,
  RelationshipStatusReadOnlyView,
  ResidencyStatusReadOnlyView,
} from '../../../Questions';
import { validateOldestStartDate as validateEmploymentHistoryOldestStartDate } from '../../ThreeYears/EmploymentHistory/utils';
import { validateOldestStartDate as validateResidentialHistoryOldestStartDate } from '../../ThreeYears/ResidentialHistory/utils';
import { AccordionContent, AccordionHeaderCircleIcon, AccordionWrapper, PaddedGridUnit } from '../_common/style';
import { Link, LinkContainer, StatusIconContainer } from './layouts.style';

const SuccessIcon = () => (
  <StatusIconContainer>
    <CheckCircle color="success.500" height="16px" width="16px" />
  </StatusIconContainer>
);

const getStatusIcon = (status?: INVITATION_STATUS) => {
  switch (status) {
    case INVITATION_STATUS.ACCEPTED:
      return <SuccessIcon />;
    case INVITATION_STATUS.EXPIRED:
    case INVITATION_STATUS.CANCELLED:
      return (
        <StatusIconContainer>
          <Error color="error.500" height="16px" width="16px" />
        </StatusIconContainer>
      );
    default:
      return;
  }
};

const getcreditHistoryStatusIcon = (creditReportCompleted: boolean) => {
  if (creditReportCompleted) {
    return <SuccessIcon />;
  }
  return (
    <StatusIconContainer>
      <Error color="error.500" height="16px" width="16px" />
    </StatusIconContainer>
  );
};

const transformInviteStatus = (status?: INVITATION_STATUS) => {
  switch (status) {
    case INVITATION_STATUS.ACCEPTED:
      return 'Complete';
    case INVITATION_STATUS.PENDING:
      return 'Pending - Invite sent';
    case INVITATION_STATUS.EXPIRED:
      return 'Invite expired';
    case INVITATION_STATUS.CANCELLED:
      return 'Invite revoked';
    default:
      return 'Not sent';
  }
};

interface ApplicantSummaryLayoutProps extends ApplicantEntityIndex {
  currentValues: FormValuesApplicants;
  goToSection: (clientId: string, section: string) => void;
}

export const ApplicantSummaryLayout = (props: ApplicantSummaryLayoutProps) => {
  const { tracking } = useAnalytics();
  const { currentValues, applicantIndex, goToSection } = props;
  const dispatch = useDispatch();
  const { [URL_PARAM_APPLICATION_ID]: urlParamApplicationId } = useParams<UseParams>();
  const customers = useCustomers();
  const [opened, setOpened] = React.useState<number[]>([0, 1, 2, 3]);
  const isLocked = useSelector(({ applications: { locked } }: ReduxStoreData) => locked);

  const applicants: StoreApplicant[] = get(currentValues, FIELD_PATHS.APPLICANTS());
  const applicant = get(currentValues, FIELD_PATHS.APPLICANT(applicantIndex));
  const ageString = getAgeFromDOB(get(currentValues, FIELD_PATHS.APPLICANT_DATE_OF_BIRTH(applicantIndex)));
  const dobString = getDisplayDateString(get(currentValues, FIELD_PATHS.APPLICANT_DATE_OF_BIRTH(applicantIndex)));
  const email = get(currentValues, FIELD_PATHS.APPLICANT_EMAIL(applicantIndex));
  const firstName = get(currentValues, FIELD_PATHS.APPLICANT_FIRST_NAME(applicantIndex));
  const lastName = get(currentValues, FIELD_PATHS.APPLICANT_LAST_NAME(applicantIndex));
  const mobile = get(currentValues, FIELD_PATHS.APPLICANT_MOBILE(applicantIndex));
  const residencyStatus = get(currentValues, FIELD_PATHS.APPLICANT_RESIDENCY_STATUS(applicantIndex));
  const applicantCustomerId = get(currentValues, FIELD_PATHS.APPLICANT_CUSTOMER_ID(applicantIndex));
  const currentCustomer = find(customers, (c) => c.id === applicantCustomerId);
  const isMobileVerified = currentCustomer?.mobileVerified;
  const isEmailVerified = currentCustomer?.emailVerified;
  const relationshipStatus = useMemo(() => {
    const maritalStatus = get(currentValues, FIELD_PATHS.APPLICANT_MARITAL_STATUS(applicantIndex));
    const maritalStatusOption = find(RELATIONSHIP_STATUS_OPTIONS, (o) => o.value === maritalStatus);
    const label = get(maritalStatusOption, 'labelAlt', get(maritalStatusOption, 'label', ''));

    if (![FIELD_VALUES.MARRIED, FIELD_VALUES.DE_FACTO].includes(maritalStatus)) {
      return label;
    }

    const preposition = maritalStatus === FIELD_VALUES.MARRIED ? 'to' : 'with';
    const marriedToId = get(currentValues, FIELD_PATHS.APPLICANT_MARITAL_DETAILS_APPLICANT_ID(applicantIndex));
    const marriedToIndex = findIndex(
      get(currentValues, FIELD_PATHS.APPLICANTS(), []),
      (a: StoreApplicant) => a.id === marriedToId
    );
    const marriedTo = get(currentValues, FIELD_PATHS.APPLICANT(marriedToIndex));
    const marriedToName = marriedTo
      ? ` (${preposition} ${marriedTo.firstName || `Applicant ${marriedToIndex + 1}`})`
      : '';

    return `${label}${marriedToName}`;
  }, [applicantIndex, currentValues]);
  const dependants = useMemo(() => {
    const hashMap = get(currentValues, FIELD_PATHS.APPLICANT_DEPENDANTS(applicantIndex), []).reduce(
      (acc: any, __: any, index: number) => {
        const guardianId = get(
          currentValues,
          FIELD_PATHS.APPLICANT_DEPENDANTS_SHARE_WITH_APPLICANT_ID(applicantIndex, index)
        );
        const key = guardianId ?? 'non-applicant';

        if (!acc[key]) {
          acc[key] = 1;
        } else {
          acc[key]++;
        }

        return acc;
      },
      {}
    );
    const guardianDependants = Object.keys(hashMap).map((key) => {
      const firstName = (() => {
        if (key === 'non-applicant') {
          return;
        }

        const aIndex = applicants.findIndex((a: StoreApplicant) => a.clientId === key);
        const firstName = get(currentValues, FIELD_PATHS.APPLICANT_FIRST_NAME(aIndex));

        return firstName;
      })();
      const dependantsCount = hashMap[key];

      return `${dependantsCount} (shared with ${firstName ?? 'non applicant'})`;
    });

    return guardianDependants;
  }, [applicantIndex, currentValues]);
  const currentResidentialAddress = get(
    currentValues,
    FIELD_PATHS.APPLICANT_CURRENT_ADDRESS_RESIDENTIAL(applicantIndex)
  );
  const currentResidentialHousingStatus = get(
    currentValues,
    FIELD_PATHS.APPLICANT_CURRENT_ADDRESS_HOUSING_STATUS(applicantIndex)
  );
  const currentResidentialStartDate = getDisplayDateString(
    get(currentValues, FIELD_PATHS.APPLICANT_CURRENT_ADDRESS_START_DATE(applicantIndex))
  );
  const isResidentialHistoryComplete = useMemo(() => {
    const { isLessThan3years } = validateResidentialHistoryOldestStartDate(applicantIndex, currentValues);

    return !isLessThan3years;
  }, []);

  const status = get(currentValues, FIELD_PATHS.APPLICANT_INVITE_STATUS(applicantIndex));
  const invitations: StoreInvitation[] = get(currentValues, FIELD_PATHS.INVITATIONS(), []);
  const invitation = getLatestInvitation(invitations, applicant.id);
  const inviteStatus = transformInviteStatus(status);
  const statusIcon = getStatusIcon(status);
  const creditReportCompleted = useMemo(() => {
    const creditReports = get(currentValues, FIELD_PATHS.CREDIT_REPORTS(), []);
    return creditReports.some(
      (creditReport: StoreCreditReport) =>
        get({ creditReports: [creditReport] }, FIELD_PATHS.CREDIT_REPORT_APPLICANT_ID(0)) === applicant.clientId &&
        get({ creditReports: [creditReport] }, FIELD_PATHS.CREDIT_REPORT_CODE(0)) ===
          CREDIT_REPORT_RESPONDS_CODE.CR_SUCCESS
    );
  }, []);

  const creditReportStatusIcon = getcreditHistoryStatusIcon(creditReportCompleted);
  const employments: StoreEmployment[] = get(currentValues, FIELD_PATHS.EMPLOYMENTS(), []);
  const applicantEmployments = employments.filter((__: any, index: number) => {
    const employmentApplicantId = get({ employments }, FIELD_PATHS.EMPLOYMENT_APPLICANT_ID(index));

    return employmentApplicantId === applicant.id;
  });
  const currentEmploymentIndex = employments.findIndex(
    (e: StoreEmployment) =>
      get({ employments: [e] }, FIELD_PATHS.EMPLOYMENT_TYPE(0)) === FIELD_VALUES.PRIMARY &&
      get({ employments: [e] }, FIELD_PATHS.EMPLOYMENT_APPLICANT_ID(0)) === applicant.id
  );

  // SE v2 incomes
  const companyClientId = get(currentValues, FIELD_PATHS.EMPLOYMENT_COMPANY_CLIENT_ID(currentEmploymentIndex));
  const companyIndex = get(currentValues, FIELD_PATHS.COMPANIES(), []).findIndex(
    (company: Company) => get({ companies: [company] }, FIELD_PATHS.COMPANY_CLIENT_ID(0)) === companyClientId
  );
  const isV2EmploymentHistoryComplete = useMemo(() => {
    const { isLessThanThreeYears } = validateEmploymentHistoryOldestStartDate(applicantEmployments);
    const hasPrimaryEmployment = employments.some(
      (employment, employmentIndex) =>
        get({ employments: [employment] }, FIELD_PATHS.EMPLOYMENT_APPLICANT_ID(0)) === applicant.id &&
        [FIELD_VALUES.PRIMARY].includes(get({ employments: [employment] }, FIELD_PATHS.EMPLOYMENT_TYPE(0)))
    );
    const completedEmployments = employments.filter(
      (employment, employmentIndex) =>
        get({ employments: [employment] }, FIELD_PATHS.EMPLOYMENT_APPLICANT_ID(0)) === applicant.id &&
        getEmploymentCompletedStatus(employmentIndex, currentValues)
    );

    const isAllEmploymentHistoryCompleted = completedEmployments.length === applicantEmployments.length;

    return !isLessThanThreeYears && hasPrimaryEmployment && isAllEmploymentHistoryCompleted;
  }, []);

  const businessName = get(currentValues, FIELD_PATHS.COMPANY_BUSINESS_NAME(companyIndex));
  const startDate = get(currentValues, FIELD_PATHS.EMPLOYMENT_START_DATE(currentEmploymentIndex));
  const employmentType = get(currentValues, FIELD_PATHS.EMPLOYMENT_EMPLOYMENT_TYPE(currentEmploymentIndex));
  const employmentBasis = get(currentValues, FIELD_PATHS.EMPLOYMENT_EMPLOYMENT_BASIS(currentEmploymentIndex));
  const employmentBusinessStructure = get(currentValues, FIELD_PATHS.COMPANY_BUSINESS_STRUCTURE(companyIndex));
  const employmentId = get(currentValues, FIELD_PATHS.EMPLOYMENT_ID(currentEmploymentIndex));
  const currentEmploymentStartDate = getDisplayDateString(startDate);
  const incomes = get(currentValues, FIELD_PATHS.INCOMES(), []);
  const selEmployedYearlyIncomeAmount = getSelfEmployedYearlyIncomeAmount(currentEmploymentIndex, currentValues);
  const yearlyIncomeAmount = useMemo(() => {
    const incomeIndex = incomes.findIndex((income: Income) => {
      const type = get({ incomes: [income] }, FIELD_PATHS.INCOME_TYPE(0));
      const incomeEmploymentId = get({ incomes: [income] }, FIELD_PATHS.INCOME_EMPLOYMENT_ID(0));

      return (
        [INCOME_TYPE.PAYG_BASE_SALARY, INCOME_TYPE.SELF_EMPLOYED_BUSINESS_INCOME].includes(type) &&
        incomeEmploymentId === employmentId
      );
    });
    const incomeAmount = get(currentValues, FIELD_PATHS.INCOME_AMOUNT(incomeIndex));
    const incomeFrequency = get(currentValues, FIELD_PATHS.INCOME_FREQUENCY(incomeIndex));

    if (!incomeAmount || !incomeFrequency) {
      return;
    }

    return convert(incomeAmount, incomeFrequency, FREQUENCY.YEARLY);
  }, []);

  const applicantEntity = get(currentValues, FIELD_PATHS.APPLICANT_ENTITY(applicantIndex), FIELD_VALUES.PERSON);

  const allTrustApplicants = applicants.filter(
    (_, index) => get(currentValues, FIELD_PATHS.APPLICANT_ENTITY(index)) === FIELD_VALUES.TRUST,
    []
  );
  const allCompanyApplicants = applicants.filter(
    (_, index) => get(currentValues, FIELD_PATHS.APPLICANT_ENTITY(index)) === FIELD_VALUES.COMPANY,
    []
  );

  const { relatedTrustApplicants, relatedCompanyApplicants } = getRelatedNonPersonApplicants(
    currentValues,
    applicantIndex
  );
  // Trustees
  const relatedTrustNames: string[] = relatedTrustApplicants.map((applicant, index) =>
    get({ applicants: [applicant] }, FIELD_PATHS.APPLICANT_TRUST_NAME(0), `Trust ${index + 1}`)
  );

  // Company directors
  const relatedCompanyApplicantNames: string[] = relatedCompanyApplicants.map((applicant, index) =>
    get({ applicants: [applicant] }, FIELD_PATHS.APPLICANT_COMPANY_NAME(0), `Company ${index + 1}`)
  );

  function handleAccordionClick(index: number, title: string) {
    let arr = [...opened];

    if (arr.includes(index)) {
      arr = arr.filter((el) => el !== index);
      tracking?.accordion.collapsed({ name: title, position: title });
    } else {
      arr.push(index);
      tracking?.accordion.expanded({ name: title, position: title });
    }

    setOpened(arr);
  }
  function handleCancelInvite() {
    if (invitation?.id) {
      dispatch(cancelInvitation(invitation.id));
    }
  }
  function handleSendInvite() {
    if (urlParamApplicationId) {
      const newInvitation = initialFormValues.INITIAL_INVITE(urlParamApplicationId, applicant.id, firstName, mobile);
      dispatch(createInvitation(newInvitation));
    }
  }

  return (
    <Grid gutter="nil">
      <PaddedGridUnit size={1}>
        <GreyedPanelText>Details</GreyedPanelText>
      </PaddedGridUnit>
      <PaddedGridUnit size={1 / 2}>
        <ApplicantsFirstNameReadOnlyView value={firstName} />
      </PaddedGridUnit>
      <PaddedGridUnit size={1 / 2}>
        <ApplicantsSurNameReadOnlyView value={lastName} />
      </PaddedGridUnit>
      <PaddedGridUnit size={1 / 2}>
        <ApplicantMobileReadOnlyView value={mobile} after={isMobileVerified && <SuccessIcon />} />
      </PaddedGridUnit>
      <PaddedGridUnit size={1 / 2}>
        <ApplicantEmailReadOnlyView value={email} after={isEmailVerified && <SuccessIcon />} />
      </PaddedGridUnit>
      <AccordionWrapper>
        <AccordionGroup>
          {/* PERSONAL INFO */}
          <Accordion isOpen={opened.includes(0)}>
            <Accordion.Header
              onClick={() => handleAccordionClick(0, 'Personal details')}
              data-component="accordion-header"
            >
              <Heading size="xxs">Personal details</Heading>
            </Accordion.Header>
            <Accordion.Content data-component="accordion-content">
              <AccordionContent>
                <Grid gutter="nil">
                  <PaddedGridUnit size={1 / 2}>
                    <DateOfBirthReadOnlyView value={dobString} />
                  </PaddedGridUnit>
                  <PaddedGridUnit size={1 / 2}>
                    <AgeReadOnlyView value={ageString} />
                  </PaddedGridUnit>
                  <PaddedGridUnit size={1}>
                    <RelationshipStatusReadOnlyView value={`${relationshipStatus}`} />
                  </PaddedGridUnit>
                  <PaddedGridUnit size={1}>
                    <ResidencyStatusReadOnlyView value={residencyStatus} />
                  </PaddedGridUnit>
                  {allTrustApplicants.length > 0 &&
                    [FIELD_VALUES.PERSON, FIELD_VALUES.COMPANY].includes(applicantEntity) && (
                      <>
                        <PaddedGridUnit size={1 / 2}>
                          <StringDisplay
                            title="Acting on behalf of trust"
                            value={relatedTrustApplicants.length ? 'Yes' : 'No'}
                          />
                        </PaddedGridUnit>
                        <PaddedGridUnit size={1 / 2}>
                          <StringsDisplay title="Trust applicants" values={relatedTrustNames} />
                        </PaddedGridUnit>
                      </>
                    )}
                  {allCompanyApplicants.length > 0 && applicantEntity === FIELD_VALUES.PERSON && (
                    <>
                      <PaddedGridUnit size={1 / 2}>
                        <StringDisplay
                          title="Director of 
                          company applicant"
                          value={relatedCompanyApplicants.length ? 'Yes' : 'No'}
                        />
                      </PaddedGridUnit>
                      <PaddedGridUnit size={1 / 2}>
                        <StringsDisplay title="Company applicants" values={relatedCompanyApplicantNames} />
                      </PaddedGridUnit>
                    </>
                  )}
                  <PaddedGridUnit size={1}>
                    <LinkContainer>
                      <Link onClick={() => goToSection(applicant.clientId, 'edit')}>View all or edit</Link>
                    </LinkContainer>
                  </PaddedGridUnit>
                </Grid>
              </AccordionContent>
            </Accordion.Content>
          </Accordion>

          {/* DEPENDANTS */}
          <Accordion isOpen={opened.includes(1)}>
            <Accordion.Header onClick={() => handleAccordionClick(1, 'Dependants')} data-component="accordion-header">
              <Heading size="xxs">Dependants</Heading>
            </Accordion.Header>
            <Accordion.Content data-component="accordion-content">
              <AccordionContent>
                <Grid gutter="nil">
                  <PaddedGridUnit size={1}>
                    <DependantsDetailsReadOnlyView title="Dependants" value={dependants} />
                  </PaddedGridUnit>
                  <PaddedGridUnit size={1}>
                    <LinkContainer>
                      <Link onClick={() => goToSection(applicant.clientId, 'edit')}>View all or edit</Link>
                    </LinkContainer>
                  </PaddedGridUnit>
                </Grid>
              </AccordionContent>
            </Accordion.Content>
          </Accordion>

          {/* 3Y RESIDENTIAL HISTORY */}
          <Accordion isOpen={opened.includes(2)}>
            <Accordion.Header
              after={<AccordionHeaderCircleIcon width="12px" height="12px" isComplete={isResidentialHistoryComplete} />}
              onClick={() => handleAccordionClick(2, '3 year residential history')}
              data-component="accordion-header"
            >
              <Heading size="xxs">3 year residential history</Heading>
            </Accordion.Header>
            <Accordion.Content data-component="accordion-content">
              <AccordionContent>
                <Grid gutter="nil">
                  <PaddedGridUnit size={1 / 2}>
                    <ApplicantAddressReadOnlyView title="Current address" address={currentResidentialAddress} />
                  </PaddedGridUnit>
                  <PaddedGridUnit size={1 / 2}>
                    <HousingStatusReadOnlyView title="Living arrangement" value={currentResidentialHousingStatus} />
                  </PaddedGridUnit>
                  <PaddedGridUnit size={1}>
                    <AddressDatesReadOnlyView title="Start Date" value={currentResidentialStartDate} />
                  </PaddedGridUnit>
                  <PaddedGridUnit size={1}>
                    <LinkContainer>
                      <Link onClick={() => goToSection(applicant.clientId, 'residential-history')}>
                        View all or edit
                      </Link>
                    </LinkContainer>
                  </PaddedGridUnit>
                </Grid>
              </AccordionContent>
            </Accordion.Content>
          </Accordion>

          {/* 3Y EMPLOYMENT HISTORY */}
          <Accordion isOpen={opened.includes(3)}>
            <Accordion.Header
              after={
                <AccordionHeaderCircleIcon width="12px" height="12px" isComplete={isV2EmploymentHistoryComplete} />
              }
              onClick={() => handleAccordionClick(3, '3 year employment history')}
              data-component="accordion-header"
            >
              <Heading size="xxs">3 year employment history</Heading>
            </Accordion.Header>
            <Accordion.Content data-component="accordion-content">
              <AccordionContent>
                <Grid gutter="nil">
                  <PaddedGridUnit size={1 / 2}>
                    <BusinessNameReadOnlyView value={businessName} />
                  </PaddedGridUnit>
                  <PaddedGridUnit size={1 / 2}>
                    <EmploymentTypeReadOnlyView value={employmentType} />
                  </PaddedGridUnit>
                  {employmentType === FIELD_VALUES.SELF_EMPLOYED ? (
                    <>
                      <PaddedGridUnit size={1 / 2}>
                        <BusinessStructureReadOnlyView value={employmentBusinessStructure} />
                      </PaddedGridUnit>
                      <PaddedGridUnit size={1 / 2}>
                        <EmploymentDatesReadOnlyView title="Start Date" value={currentEmploymentStartDate} />
                      </PaddedGridUnit>
                      <PaddedGridUnit size={1 / 2}>
                        <AnnualIncomeReadOnlyView title="Self-employed income" value={selEmployedYearlyIncomeAmount} />
                      </PaddedGridUnit>
                    </>
                  ) : (
                    <>
                      <PaddedGridUnit size={1 / 2}>
                        <EmploymentBasisReadOnlyView value={employmentBasis} />
                      </PaddedGridUnit>
                      <PaddedGridUnit size={1 / 2}>
                        <EmploymentDatesReadOnlyView title="Start Date" value={currentEmploymentStartDate} />
                      </PaddedGridUnit>
                      <PaddedGridUnit size={1 / 2}>
                        <AnnualIncomeReadOnlyView title="Base salary" value={yearlyIncomeAmount} />
                      </PaddedGridUnit>
                    </>
                  )}

                  <PaddedGridUnit size={1}>
                    <LinkContainer>
                      <Link onClick={() => goToSection(applicant.clientId, 'employment-history')}>
                        View all or edit
                      </Link>
                    </LinkContainer>
                  </PaddedGridUnit>
                </Grid>
              </AccordionContent>
            </Accordion.Content>
          </Accordion>
        </AccordionGroup>
      </AccordionWrapper>

      {/* CUSTOMER INVITE */}
      <>
        {applicantEntity === FIELD_VALUES.PERSON && (
          <PaddedGridUnit size={1 / 2}>
            <InviteStatusReadOnlyView after={statusIcon} value={inviteStatus} />
            <InviteStatusReadOnlyViewAccompaniment
              status={status}
              canInvite={!!mobile && !!firstName && !isLocked}
              cancelInvite={handleCancelInvite}
              sendInvite={handleSendInvite}
            />
          </PaddedGridUnit>
        )}

        {/* CREDIT REPORTS */}
        <PaddedGridUnit size={1 / 2}>
          <CreditReportReadOnlyTeamView
            after={creditReportStatusIcon}
            value={creditReportCompleted ? 'Complete' : 'Outstanding'}
          />
        </PaddedGridUnit>
      </>
    </Grid>
  );
};
